// src/hooks/useGuestChat.ts
//
// GUEST CHAT TEASER — NON-INDIAN (USA / foreign) VISITORS ONLY.
//
// The funnel: a logged-out foreign visitor can open /chat, ask ONE question and
// see a blurred (dummy — zero API calls) answer. Tapping "View for free" opens
// the login sheet (Google is already offered to non-Indian users there); after
// signing in they create their profile and the question they asked as a guest is
// then sent for real, so they watch their answer generate.
//
// Indian visitors are UNAFFECTED: `guestChatEnabled` is false for them, so every
// chat entry point still opens the auth modal and /chat still bounces logged-out
// users home — exactly as before.

import { useAuth } from "../context/AuthContext";

// The guest's question, parked across the login redirect (Google OAuth is a full
// page navigation, so in-memory state is lost) and the profile-creation step.
// sessionStorage, not localStorage: it must not outlive the tab.
export const GUEST_PENDING_QUESTION_KEY = "auraGuestPendingQuestion";

// Don't resurrect a stale question. If the visitor wandered off for an hour and
// came back, silently drop it rather than billing a question they forgot asking.
const PENDING_MAX_AGE_MS = 60 * 60 * 1000;

// react-snap prerenders /chat at build time in a headless browser. Force the
// guest funnel OFF there so the generated snapshot stays byte-identical to
// today's (SEO-safe) — the real decision is made in the visitor's browser.
const isPrerender = () =>
  typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

/**
 * Which destinations a guest may reach without an account. ONLY the plain chat
 * screen: `/chat?startCall=1` (voice call) still needs an account, because a
 * call bills per minute against a real wallet and needs a birth chart.
 */
export const isGuestChatPath = (path: string) => path === "/chat";

export function useGuestChat() {
  const { user, userCountry, geoChecked } = useAuth();

  // Same signal the login sheet uses to offer Google, and the one Session Replay
  // uses to gate "USA": the geoapify-detected country of a logged-out visitor.
  const isForeign = !!userCountry && userCountry.toUpperCase() !== "IN";

  return {
    // A guest may enter /chat and see the teaser.
    guestChatEnabled: !user && geoChecked && isForeign && !isPrerender(),
    // Geo probe still in flight — a route gate must WAIT rather than assume
    // "Indian" and bounce a USA visitor home. (userCountry defaults to 'IN', so
    // "not yet resolved" and "resolved as India" look identical without this.)
    resolvingGeo: !user && !geoChecked && !isPrerender(),
  };
}

export function savePendingGuestQuestion(question: string) {
  try {
    sessionStorage.setItem(
      GUEST_PENDING_QUESTION_KEY,
      JSON.stringify({ q: question, ts: Date.now() }),
    );
  } catch {
    /* storage unavailable — the user just re-asks after signing in */
  }
}

/** Read AND clear the parked question. Clearing first keeps it single-use, so a
 *  remount / re-render can never re-send (and re-bill) the same question. */
export function takePendingGuestQuestion(): string | null {
  try {
    const raw = sessionStorage.getItem(GUEST_PENDING_QUESTION_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(GUEST_PENDING_QUESTION_KEY);
    const { q, ts } = JSON.parse(raw);
    if (!q || typeof q !== "string") return null;
    if (!ts || Date.now() - ts > PENDING_MAX_AGE_MS) return null;
    return q;
  } catch {
    return null;
  }
}

/** Peek without consuming — for UI that just wants to know one is waiting. */
export function hasPendingGuestQuestion(): boolean {
  try {
    return !!sessionStorage.getItem(GUEST_PENDING_QUESTION_KEY);
  } catch {
    return false;
  }
}
